import {
  Component,
  OnInit,
  EventEmitter,
  Input,
  Output,
  ElementRef,
  ViewChild,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
  AfterViewChecked
} from '@angular/core';
import { FormBuilder, NgForm, Validators } from '@angular/forms';
import { CodeInputComponent } from 'angular-code-input';
import { DictionaryService, GoogleTagManagerService } from '@cudirect/shared-ui-library';
import { InstaTouchState, InstaTouchSession } from '@shared/model/instatouch.model';
import { LenderService } from '@shared/service/lender/lender.service';
import { StorageService } from '@workflow/service/storage/storage.service';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { ApplicationApiService } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.service';
import { FieldInstance } from '@workflow/class/field.service';
import { AnalyticsMetadataService } from '@app/service/analytics-metadata.service';
import { PageInstance } from '@workflow/class/page.service';

@Component({
  selector: 'dx-instatouch',
  templateUrl: './instatouch.component.html',
  styleUrls: ['./instatouch.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class InstatouchComponent implements OnInit, OnChanges, AfterViewChecked {
  @ViewChild('codeInput') codeInput: CodeInputComponent;
  @ViewChild('instaTouch') instaTouch: NgForm;
  /** Input to indicate Instatouch buuilding block field */
  @Input() public autofillField: FieldInstance;
  @Input() public instaTouchState: InstaTouchState;
  @Input() public lockMobileNumber = false;
  @Input() public errorMessage: string;
  /** flag to indicate Api call in progress */
  @Input() public isLoading: boolean;
  @Input() public showCodeSentAlert?: boolean;
  @Output() public instaTouchStateChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() public getPassCode: EventEmitter<InstaTouchSession> = new EventEmitter<InstaTouchSession>();
  @Output() public validatePassCode: EventEmitter<InstaTouchSession> = new EventEmitter<InstaTouchSession>();
  @Output() public getPersonalInfo: EventEmitter<InstaTouchSession> = new EventEmitter<InstaTouchSession>();
  /** emit event to show manual card */
  @Output() public proceedWithoutPhone = new EventEmitter<boolean>();
  /** fiel ref for mobile number */
  @ViewChild('input') public input: ElementRef;
  /** Mobile number input field */
  @Input() public mobileNumber: string;
  /** ssnNumber input field*/
  public ssnNumber: number;
  /** zipCode input field */
  public zipCode: number;
  /** Passcode input field */
  public passcode: string;
  public showNewCodeSentAlert = false;
  public isCoapplicant = false;
  public isSecondCoapplicant = false;
  constructor(
    private readonly dictionary: DictionaryService,
    private readonly lenderService: LenderService,
    private readonly formBuilder: FormBuilder,
    private readonly storageService: StorageService,
    private readonly workflowService: WorkflowService,
    public readonly applicationApiService: ApplicationApiService,
    private readonly analyticsService: AnalyticsMetadataService,
    private readonly gtm: GoogleTagManagerService
  ) {
    const page = this.workflowService.currentPage;
    // Set if the instance is for a co-applicant
    // This works for both OLA and OAO
    this.isCoapplicant = page?.config?.id?.toLowerCase().includes('co-applicant');
    this.isSecondCoapplicant = page?.config?.id?.toLowerCase().includes('additional-co-app');
  }

  public readonly stages: { [key: string]: InstaTouchState } = {
    initial: 'initial',
    passcode: 'passcode',
    identity: 'identity',
    completed: 'completed'
  };

  /** get currentStage */
  public get currentStage(): InstaTouchState {
    return this.instaTouchState;
  }

  /** set currentStage */
  public set currentStage(value) {
    this.instaTouchState = value;
    this.instaTouchStateChange.emit(value);
  }

  /** return mobile number without Mask */
  public get unMaskedNumber(): string {
    return this.mobileNumber ? this.mobileNumber.replace(/[-() ]/g, '') : '';
  }

  /** mobile number length */
  public get validMobileNumber(): boolean {
    const length = 10;
    return this.unMaskedNumber.length === length;
  }

  /** methods to display lenderName on disclaimer */
  public get disclaimer(): string {
    const lenderName = this.lenderService?.lenderData?.lenderName;
    return this.dictionary.read('personalInfoAutofill/disclaimer')?.replace(/{lenderName}/gi, lenderName);
  }

  /** getter prop to credit score info when ssn verification */
  public get creditScoreInfo(): string {
    return this.dictionary.read('personalInfoAutofill/creditScoreInfo');
  }

  /**
   * get identity error message
   */
  public get getIdentityErrorMessage(): string {
    return `${this.dictionary.read('personalInfoAutofill/identityError')}
    ${this.proceedWithoutPhone.observers?.length !== 0 ? this.dictionary.read('personalInfoAutofill/identityErrorAction') : ''}.
    ${this.dictionary.read('personalInfoAutofill/Offramp2')}`;
  }

  public ngOnInit() {
    this.lockMobileNumber = this.lockMobileNumber && this.validMobileNumber;
    this.reset();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.instaTouchState) {
      this.scrolltoTop();
    }
    // clear passcode input if error message is present
    if (this.currentStage === this.stages.passcode && this.errorMessage) {
      this.clearCodeInput();
    }
  }

  public ngAfterViewChecked(): void {
    if (this.currentStage === this.stages.passcode) {
      this.verificationInputWCAG();
    }
  }
  private reset(): void {
    this.currentStage = this.stages.initial;
    this.isLoading = false;
    this.errorMessage = null;
  }

  private scrolltoTop(): void {
    window.scrollTo(0, 0);
  }

  /**
   * Show the mobile number with asterisks format to user.
   */
  public getMobileNumberWithAsterisks(mobileNumber: string): string {
    return mobileNumber && '(***) ***-' + mobileNumber.substring(mobileNumber.length - 4);
  }
  /**
   * Method to set WCAG attributes for verification code input fields.
   */
  private verificationInputWCAG(): void {
    if (!this.codeInput) return;
    // eslint-disable-next-line @typescript-eslint/dot-notation
    for (let i = 0; i < this.codeInput['inputs']?.length; i++) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.codeInput['inputs'][i].id = 'code' + (i + 1);
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.codeInput['inputs'][i].setAttribute('aria-label', 'Digit ' + (i + 1) + ' of 6 digit verification code');
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.codeInput['inputs'][i].setAttribute('aria-describedby', 'code_error');
    }
  }

  public clearCodeInput(): void {
    if (!this.codeInput) return;
    // eslint-disable-next-line @typescript-eslint/dot-notation
    for (let i = 0; i < this.codeInput['inputs']?.length; i++) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.codeInput['inputs'][i].value = '';
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.codeInput['inputs'][i].className = '';
    }
    setTimeout(() => {
      this.codeInput.inputsList?.first?.nativeElement?.focus();
    }, 500);
  }

  /**
   * Fired every time the code input changes.
   * @param e the current code entry
   */
  public onCodeChanged(e) {
    this.passcode = e;
    if (!this.instaTouch?.form?.controls?.codeInput) {
      // Programatically add a passcode field into ngform
      this.instaTouch?.form?.addControl(
        'codeInput',
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.formBuilder.control('', [Validators.required, Validators.maxLength(6), Validators.minLength(6)])
      );
    }
    this.instaTouch?.form?.controls?.codeInput.setValue(this.passcode);
  }

  /**
   * Fires once the code input is complete.
   * @param e the current code entry
   */
  public onCodeCompleted(e) {
    this.passcode = e;
    this.emitEvent(this.instaTouch);
  }

  public emitEvent(ngForm: NgForm): void {
    ngForm.form.markAllAsTouched();
    if (ngForm.invalid) return;
    const data = {
      isPrimaryApplicant: !this.isCoapplicant
    } as InstaTouchSession;
    switch (this.currentStage) {
      case this.stages.initial:
        data.mobileNumber = this.unMaskedNumber;
        const metadata = this.analyticsService.getMetadata({ title: 'Instatouch - Start', id: 'instatouch-page' } as PageInstance);
        this.gtm.publishEvent(metadata[0], metadata[1]);
        this.getPassCode.emit(data);
        break;
      case this.stages.passcode:
        data.passCode = this.passcode;
        this.validatePassCode.emit(data);
        break;
      case this.stages.identity:
        data.ssn = this.ssnNumber;
        data.zipCode = this.zipCode;
        const metadata2 = this.analyticsService.getMetadata({ title: 'Instatouch - End', id: 'instatouch-page' } as PageInstance);
        this.gtm.publishEvent(metadata2[0], metadata2[1]);
        this.getPersonalInfo.emit(data);
        break;
    }
    this.showNewCodeSentAlert = false;
    ngForm.resetForm(ngForm.value);
  }

  public getNewPasscode(): void {
    const data = {
      isPrimaryApplicant: !this.isCoapplicant,
      mobileNumber: this.unMaskedNumber
    } as InstaTouchSession;

    this.clearCodeInput();
    this.showNewCodeSentAlert = true;
    this.getPassCode.emit(data);
  }

  /**
   * Emit the event when user clicks manual flow link option from insta touch card.
   */
  public proceedWithoutPhonePrefill(): void {
    this.proceedWithoutPhone.emit(true);
  }

  public getHeadingLabel(label: string): string {
    if (this.isCoapplicant) {
      return this.dictionary.read('personalInfoAutofill/' + label + 'Coapp');
    } else if (this.isSecondCoapplicant) {
      return this.dictionary.read('personalInfoAutofill/' + label + 'SecondCoapp');
    } else {
      return this.dictionary.read('personalInfoAutofill/' + label);
    }
  }

  get cardDescription(): string {
    if (this.isSecondCoapplicant) {
      return this.dictionary.read('personalInfoAutofill/instatouchDescriptionSecondCoapp');
    } else if (this.isCoapplicant) {
      return this.dictionary.read('personalInfoAutofill/instatouchDescriptionCoapp');
    } else {
      return this.dictionary.read('personalInfoAutofill/instatouchDescription');
    }
  }
}
